/**
 * WP pages often store navigation sidebar tags on `categories` with
 * taxonomy "post_tag" instead of `tags`. Collect IDs from both sources.
 *
 * Supports two tag formats:
 *   - Object with an `id` field:  { id: 28768, name: "...", slug: "..." }
 *   - Plain numeric ID:           28768
 */
export function collectSidebarTagIds(post) {
	if (!post) return [];

	const seen = new Set();
	const ids = [];

	const add = (term) => {
		// Support both { id: number } objects and bare numeric IDs
		const id = typeof term === "number" ? term : term?.id;
		if (id == null || seen.has(id)) return;
		seen.add(id);
		ids.push(id);
	};

	for (const tag of post.tags ?? []) {
		add(tag);
	}

	for (const category of post.categories ?? []) {
		// Object-shaped categories: only include post_tag taxonomy entries
		if (typeof category === "object" && category?.taxonomy === "post_tag") {
			add(category);
		}
	}

	return ids;
}

export const useSidebarSorter = async (tags_arr) => {
	const { env } = useRuntimeConfig().public;

	const checkAndSetSidebarTag = (item) => {
		if (item === 28766) {
			// { title: 'navigation-sidebar-options', id: 28765 }
			return 'navigation-sidebar-options';
		}
		if (item === 28774) {
			// { title: 'navigation-sidebar-short-selling', id: 28773 }
			return 'navigation-sidebar-short-selling';
		}

		if (item === 28781) {
			// { title: 'navigation-sidebar-advanced-trading', id: 28780 }
			return 'navigation-sidebar-advanced-trading';
		}
		if (item === 28779) {
			// { title: 'navigation-sidebar-guides', id: 28775 }
			return 'navigation-sidebar-guides';
		}

		if (item === 28761 || item === 28782) {
			return 'navigation-sidebar-under';
		}

		if (item === 28768) {
			return 'navigation-sidebar-penny-stocks-on';
		}

		if (item === 28769) {
			// { title: 'navigation-sidebar-sector', id: 28764 }
			return 'navigation-sidebar-sector';
		}

		if (item === 28772) {
			// { title: 'navigation-sidebar', id: 28757 }

			return 'navigation-sidebar';
		}
		if (item === 28786) {
			return 'navigation-sidebar-legends-of-trading';
		}
		// If item doesn't match any of the predefined values, return null or handle as needed
		return null;
	};

	const getSidebarId = (tagsArray) => {

		if (!Array.isArray(tagsArray)) {
			return null
		}

		for (const tag of [...tagsArray]) {

			const sidebarId = checkAndSetSidebarTag(tag);
			if (sidebarId !== null)
				return sidebarId;
		}

		// If no matching sidebar ID is found, return a default value or handle as needed
		return null;
	};

	const sidebarId = getSidebarId(tags_arr);

	if (!sidebarId) {
		if (store.contentSidebar.show) store.toggleContentSidebarVisibility(false);

		if (env !== 'production') {
			console.error("No matching sidebar ID found.");
		}
		return null;
	}

	try {

		if (store.contentSidebar.sidebarId === sidebarId && store.contentSidebar.data) {
			if (!store.contentSidebar.show) store.toggleContentSidebarVisibility(true);
			return
		};

		const sidebar = await fetch(`/js/menus/${sidebarId}.json`).then(res => {
			if (res.ok) {
				return res.json()
			}
		});

		if (sidebar) {

			const payload = [...sidebar.items];
			store.toggleContentSidebarVisibility(true);
			store.setContentSidebarData(payload);
			store.setContentSidebarId(sidebarId);

			return { sidebar: payload, sidebarId };
		}
		console.error("Fetch returned no data.");
	} catch (error) {
		console.error("Error fetching data:", error);
	}
	return null;
};
